<template>
  <div 
    ref="containerRef"
    class="relative h-full flex gap-6 p-6"
    @mousemove="handleGlobalMouseMove"
    @mouseup="handleCircleMouseUp"
  >
    <!-- Left: Color Controls -->
    <div v-if="hasBridges" class="flex flex-col gap-4 items-center shrink-0">
      <!-- Hue Circle (Color wheel) -->
      <ColorCircleContainer
        ref="hueCircleContainerRef"
        type="hue"
        :size="circleSize"
        @mouse-down="(e) => handleCircleMouseDown(e, 'hue')"
      >
        <LightPipsLayer
          :singles-with-positions="singlesInHueCircle"
          :stacks="stacksInHueCircle"
          :circle-size="circleSize"
          :circle-radius="circleSize / 2"
          :scene-id="currentSceneId"
          :is-selected="selection.isSelected"
          :selected-light-ids="selectedLightIds"
          @pip-mouse-down="(e, light) => handlePipMouseDown(e, light, 'hue')"
          @pip-click="selection.toggleItem"
          @gradient-stop-drag-start="handleGradientStopDragStart"
          @gradient-stop-click="handleGradientStopClick"
          @stack-drag-start="(e, lights) => handleStackDragStart(e, lights, 'hue')"
          @stack-click="handleStackClick"
          @disband-stack="handleDisbandStack"
        />
      </ColorCircleContainer>
      
      <!-- CCT Slider (1D temperature control) -->
      <ColorTemperatureSlider
        ref="cctSliderContainerRef"
        :width="circleSize"
        @mouse-down="(e) => handleSliderMouseDown(e, 'white')"
      >
        <SliderPipsLayer
          :lights-with-positions="lightsInCctSlider"
          :is-selected="selection.isSelected"
          @pip-mouse-down="(e, light) => handlePipMouseDown(e, light, 'white')"
          @pip-click="selection.toggleItem"
        />
      </ColorTemperatureSlider>
      
      <!-- Brightness Slider below color controls -->
      <BrightnessSlider
        :key="selection.selectedItems.value.map(i => i.id).join('-')"
        :selected-items="selection.selectedItems.value"
        @brightness-change="handleBrightnessChange"
      />
    </div>
    
    <!-- Right: Light Cards in scroll area -->
    <ScrollArea class="flex-1 h-full">
      <div class="grid grid-cols-2 gap-3">
        <CompactLightCard
          v-for="light in lightsStore.setupLights"
          :key="light.id"
          :light="light"
          :scene-id="currentSceneId"
          :is-selected="selection.isSelected(light)"
          @click="handleCardClick(light)"
          @brightness-change="(b) => handleCardBrightnessChange(light, b)"
        />
      </div>
    </ScrollArea>
    
    <!-- Selection Rectangle (shown while dragging to select) -->
    <SelectionRectangle
      v-if="rectangleSelection.selectionRect.value"
      :is-visible="rectangleSelection.isSelecting.value"
      :start-x="rectangleSelection.selectionRect.value.startX"
      :start-y="rectangleSelection.selectionRect.value.startY"
      :end-x="rectangleSelection.selectionRect.value.endX"
      :end-y="rectangleSelection.selectionRect.value.endY"
    />

    <!-- Pip Drag Name Display (shown when dragging individual lights) -->
    <div
      v-if="isDraggingPip && draggedLightName"
      class="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-background/95 backdrop-blur-sm border border-border rounded-lg px-4 py-2 shadow-lg z-50 pointer-events-none"
    >
      <p class="text-sm font-medium text-foreground">{{ draggedLightName }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { logger } from '@/lib/logger'
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'
import { Card, CardContent } from '@/components/ui/card'
import { useLightsStore } from '@/stores/lights'
import { useSceneLightConfigsStore } from '@/stores/sceneLightConfigs'
import { useHueBridgesStore } from '@/stores/hueBridges'
import { useWledDevicesStore } from '@/stores/wledDevices'
import { wledBackgroundService } from '@/services/wled/WledBackgroundService'
import { useLightPositioning } from '@/composables/useLightPositioning'
import { useLightSelection } from '@/composables/useLightSelection'
import { useLightColorMode } from '@/composables/useLightColorMode'
import { useCircleDrag, type CircleDragHandler } from '@/composables/useCircleDrag'
import { useRectangleSelection } from '@/composables/useRectangleSelection'
import { useLightPresenceSync } from '@/composables/useLightPresenceSync'
import ColorCircleContainer from './ColorCircleContainer.vue'
import ColorTemperatureSlider from './ColorTemperatureSlider.vue'
import LightPipsLayer from './LightPipsLayer.vue'
import SliderPipsLayer from './SliderPipsLayer.vue'
import BrightnessSlider from './BrightnessSlider.vue'
import CompactLightCard from './CompactLightCard.vue'
import SelectionRectangle from './SelectionRectangle.vue'
import { ScrollArea } from '@/components/ui/scroll-area'
import { worldToScreen, getCircleContext } from '@/lib/coordinateUtils'
import { clampToCircle, xyToHSV, hsvToHueState } from '@/lib/colorConversion'
import type { Light } from '@/stores/lights'

const route = useRoute()
const lightsStore = useLightsStore()
const sceneLightConfigsStore = useSceneLightConfigsStore()
const hueBridgesStore = useHueBridgesStore()
const wledDevicesStore = useWledDevicesStore()

const circleSize = 300
const containerRef = ref<HTMLElement>()
const hueCircleContainerRef = ref()
const cctSliderContainerRef = ref()

// Current scene
const currentSceneId = computed(() => route.params.sceneId as string)

// Check if there are any bridges or WLED devices (to show color circles)
const hasBridges = computed(() => {
  const bridgeCount = hueBridgesStore.allBridges.length
  const deviceCount = wledDevicesStore.allDevices.length
  const lightCount = lightsStore.setupLights.length
  // Show circles if we have ANY lights in setup (even without bridges)
  return bridgeCount > 0 || deviceCount > 0 || lightCount > 0
})

// Presence sync composable
const presenceSync = useLightPresenceSync(currentSceneId)

// Composables
const selection = useLightSelection()
const colorMode = useLightColorMode(currentSceneId)

// Drag state
const activeDragType = ref<'hue' | 'white' | null>(null)
const isDraggingPip = ref(false)

// Gradient stop drag state
const isDraggingGradientStop = ref(false)
const draggingGradientLight = ref<Light | null>(null)
const draggingGradientStopId = ref<string | null>(null)

// Drag override for light positioning - forces lights to appear in target circle during drag
const dragOverride = computed(() => ({
  lightIds: isDraggingPip.value 
    ? new Set(selection.selectedItems.value.map(item => item.id))
    : new Set<string>(),
  targetCircle: activeDragType.value
}))

// Create drag handlers for each circle
const hueDrag = useCircleDrag({
  circleType: 'hue',
  currentSceneId,
  selectedItems: selection.selectedItems
})

const whiteDrag = useCircleDrag({
  circleType: 'white',
  currentSceneId,
  selectedItems: selection.selectedItems
})

// Get current drag handler
const currentDrag = computed((): CircleDragHandler | null => {
  if (activeDragType.value === 'hue') return hueDrag
  if (activeDragType.value === 'white') return whiteDrag
  return null
})

// Positioning composable
const { 
  lightsInHueCircle, 
  lightsInWhiteCircle, 
  lightsInCctSlider,
  stacksInHueCircle,
  singlesInHueCircle
} = useLightPositioning(
  computed(() => lightsStore.setupLights),
  currentSceneId,
  dragOverride
)

// Selected light IDs as a Set (for stacked pips)
const selectedLightIds = computed(() => new Set(selection.selectedItems.value.map(item => item.id)))

// Rectangle selection
const rectangleSelection = useRectangleSelection()

// Dragged light name (only show for individual lights, not groups)
const draggedLightName = computed(() => {
  if (!isDraggingPip.value || selection.selectedItems.value.length !== 1) {
    return null
  }
  
  const item = selection.selectedItems.value[0]
  return item.name
})


// Handler functions (moved from controller)
/**
 * Handle brightness change for selected items
 * Broadcasts via presence to all connected clients
 */
const handleBrightnessChange = (brightness: number) => {
  if (!currentSceneId.value) return
  
  for (const item of selection.selectedItems.value) {
    presenceSync.syncLightState(item.id, { brightness })
  }
}

/**
 * Handle brightness change from individual light card
 * Turns light off when brightness is 0, on otherwise
 * Broadcasts via presence to all connected clients
 */
const handleCardBrightnessChange = (light: Light, brightness: number) => {
  if (!currentSceneId.value) return
  
  const shouldBeOn = brightness > 0
  
  // Broadcast via presence
  presenceSync.syncLightState(light.id, { 
    brightness,
    on: shouldBeOn
  })
}

/**
 * Move selected items to white mode
 * Fire-and-forget - don't block the drag operation
 */
const moveSelectedToWhite = () => {
  logger.info(`[Canvas] Moving selected items to WHITE mode (non-blocking)`)
  // Don't await - let this run in background without blocking drag
  Promise.all(
    selection.selectedItems.value.map(async (item) => {
      if (colorMode.isInColorMode(item)) {
        logger.info(`  -> Switching ${item.name} to white mode`)
        await colorMode.toggleColorMode(item, true)
      }
    })
  ).then(() => {
    logger.info(`[Canvas] WHITE mode switch complete`)
  }).catch(err => {
    logger.error('Failed to switch to white mode:', err)
  })
}

/**
 * Move selected items to color mode
 * Fire-and-forget - don't block the drag operation
 */
const moveSelectedToColor = () => {
  logger.info(`[Canvas] Moving selected items to COLOR mode (non-blocking)`)
  // Don't await - let this run in background without blocking drag
  Promise.all(
    selection.selectedItems.value.map(async (item) => {
      if (!colorMode.isInColorMode(item)) {
        logger.info(`  -> Switching ${item.name} to color mode`)
        await colorMode.toggleColorMode(item, false)
      }
    })
  ).then(() => {
    logger.info(`[Canvas] COLOR mode switch complete`)
  }).catch(err => {
    logger.error('Failed to switch to color mode:', err)
  })
}

/**
 * Refresh all data
 */
const refreshData = async () => {
  await sceneLightConfigsStore.fetchConfigs()
  await lightsStore.fetchLights()
}

/**
 * Initialize data
 */
const initialize = async () => {
  await lightsStore.fetchLights()
  
  if (currentSceneId.value) {
    await sceneLightConfigsStore.fetchConfigs()
  }
}


// Event handlers for UI interactions
const handlePipMouseDown = (event: MouseEvent, item: Light, circleType: 'hue' | 'white') => {
  event.preventDefault()
  event.stopPropagation()
  
  isDraggingPip.value = true
  
  // Preserve multi-selection when dragging selected item
  if (!selection.isSelected(item)) {
    // Use Shift or Meta (Cmd/Ctrl) to add to selection
    if (event.shiftKey || event.metaKey) {
      selection.selectItem(item)
    } else {
      selection.selectOnly(item)
    }
  }
  // If item is already selected, don't change selection (allows dragging multiple)
  
  activeDragType.value = circleType
  currentDrag.value?.startDrag()
}

/**
 * Handle gradient stop drag start
 * Sets up state for dragging a specific gradient stop
 */
const handleGradientStopDragStart = (
  event: MouseEvent, 
  light: Light, 
  stopId: string, 
  _color: { hue: number; saturation: number; brightness: number }
) => {
  event.preventDefault()
  event.stopPropagation()
  
  isDraggingGradientStop.value = true
  draggingGradientLight.value = light
  draggingGradientStopId.value = stopId
  activeDragType.value = 'hue' // Gradient stops are always on hue circle
  
  // Select the light containing this gradient
  if (!selection.isSelected(light)) {
    selection.selectOnly(light)
  }
}

/**
 * Handle gradient stop click (for selection)
 */
const handleGradientStopClick = (light: Light, _stopId: string) => {
  selection.toggleItem(light)
}

/**
 * Handle drag start for stacked lights
 */
const handleStackDragStart = (event: MouseEvent, lights: Light[], circleType: 'hue' | 'white') => {
  event.preventDefault()
  event.stopPropagation()
  
  isDraggingPip.value = true
  
  // Select all lights in the stack
  if (!event.shiftKey && !event.metaKey) {
    selection.clearSelection()
  }
  for (const light of lights) {
    selection.selectItem(light)
  }
  
  activeDragType.value = circleType
  currentDrag.value?.startDrag()
}

/**
 * Handle click on stacked lights (select all in stack)
 */
const handleStackClick = (lights: Light[]) => {
  // Select all lights in the stack
  selection.clearSelection()
  for (const light of lights) {
    selection.selectItem(light)
  }
}

/**
 * Handle disbanding a stack - spread lights apart
 */
const handleDisbandStack = (lights: Light[]) => {
  console.log('[Disband] Called with', lights.length, 'lights')
  if (!currentSceneId.value || lights.length === 0) return
  
  // Get the center position from the first light's current state
  const firstLight = lights[0]
  const currentState = firstLight.current_state
  const baseHue = currentState?.hue ?? 0
  const baseSat = currentState?.sat ?? 200 // Default to fairly saturated
  
  console.log('[Disband] Base hue:', baseHue, 'Base sat:', baseSat)
  
  // Spread lights in a circle pattern around the center
  // Use a larger spread to ensure they're not immediately re-grouped
  const hueSpreadDegrees = 30 // 30 degrees between lights
  lights.forEach((light, index) => {
    // Offset in hue space (0-65535 represents 0-360 degrees)
    const hueOffset = (index - (lights.length - 1) / 2) * hueSpreadDegrees * (65535 / 360)
    
    const newHue = Math.max(0, Math.min(65535, Math.round(baseHue + hueOffset)))
    
    console.log(`[Disband] Light ${light.name}: hue ${newHue}`)
    
    // Update local store immediately for instant feedback
    lightsStore.updateCurrentState(light.id, {
      hue: newHue,
      sat: baseSat
    })
    
    // Also broadcast to other clients
    presenceSync.syncLightState(light.id, {
      hue: newHue,
      saturation: baseSat
    })
  })
}

const handleCircleMouseDown = (event: MouseEvent, circleType: 'hue' | 'white') => {
  // Start rectangle selection if clicking on empty area (not on a pip)
  const target = event.target as HTMLElement
  const isCanvas = target.tagName === 'CANVAS' || target.classList.contains('circle-container')
  
  if (isCanvas && containerRef.value) {
    // Convert viewport coordinates to container-relative coordinates
    const rect = containerRef.value.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    
    rectangleSelection.startSelection(x, y)
    
    if (!event.shiftKey) {
      selection.clearSelection()
    }
  }
}

/**
 * Detect which circle/slider (if any) the cursor is currently over
 */
const detectCircleUnderCursor = (clientX: number, clientY: number): 'hue' | 'white' | null => {
  const hueContainer = hueCircleContainerRef.value?.containerRef
  const cctContainer = cctSliderContainerRef.value?.containerRef
  
  if (hueContainer) {
    const rect = hueContainer.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    const radius = rect.width / 2
    const dx = clientX - centerX
    const dy = clientY - centerY
    const distance = Math.sqrt(dx * dx + dy * dy)
    
    if (distance <= radius) {
      return 'hue'
    }
  }
  
  // Check CCT slider (rectangular area)
  if (cctContainer) {
    const rect = cctContainer.getBoundingClientRect()
    if (clientX >= rect.left && clientX <= rect.right &&
        clientY >= rect.top && clientY <= rect.bottom) {
      return 'white'
    }
  }
  
  return null
}

/**
 * Global mouse move handler - enables cross-circle dragging
 */
const handleGlobalMouseMove = async (event: MouseEvent) => {
  const hueContainer = hueCircleContainerRef.value?.containerRef
  const cctContainer = cctSliderContainerRef.value?.containerRef
  
  // Handle gradient stop dragging
  if (isDraggingGradientStop.value && draggingGradientLight.value && draggingGradientStopId.value) {
    if (!hueContainer) return
    
    const rect = hueContainer.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    const radius = rect.width / 2
    
    const dx = event.clientX - centerX
    const dy = event.clientY - centerY
    
    const coord = {
      x: dx / radius,
      y: dy / radius
    }
    
    // Clamp to circle
    const clamped = clampToCircle(coord)
    
    // Convert to hue/saturation
    const hsv = xyToHSV(clamped)
    const hueState = hsvToHueState(hsv)
    
    // Update the gradient stop color in the scene's wled_effect_settings
    const lightId = draggingGradientLight.value.id
    const stopId = draggingGradientStopId.value
    
    // Get resolved config (includes inheritance) to access gradient stops
    const resolvedConfig = sceneLightConfigsStore.getResolvedConfig(currentSceneId.value, lightId)
    
    if (resolvedConfig?.wled_effect_settings?.gradient?.stops) {
      const updatedStops = resolvedConfig.wled_effect_settings.gradient.stops.map(stop => {
        if (stop.id === stopId) {
          return {
            ...stop,
            color: {
              hue: hueState.hue,
              saturation: hueState.saturation,
              brightness: stop.color.brightness // Keep existing brightness
            }
          }
        }
        return stop
      })
      
      const updatedEffectSettings = {
        ...resolvedConfig.wled_effect_settings,
        gradient: { stops: updatedStops }
      }
      
      // Update store - this will create a local override if inherited
      await sceneLightConfigsStore.updateWledEffectSettings(currentSceneId.value, lightId, updatedEffectSettings)
      
      // Also apply to the WLED device in real-time
      await wledBackgroundService.applyEffectSettingsToLight(lightId, updatedEffectSettings as any)
    }
    
    return
  }
  
  // Handle pip dragging with automatic circle switching
  if (isDraggingPip.value && currentDrag.value?.isDragging.value) {
    // Detect which circle we're over
    const hoveredCircle = detectCircleUnderCursor(event.clientX, event.clientY)
    
    // If hovering over a different circle, switch to it!
    if (hoveredCircle && hoveredCircle !== activeDragType.value) {
      logger.info(`🔄 [Canvas] Circle switch detected: ${activeDragType.value} → ${hoveredCircle}`)
      
      // Cancel pending updates from old circle - this broadcasts them WITH mode clearing
      await currentDrag.value?.cancelPendingUpdates(hoveredCircle)
      
      // Switch to new circle
      activeDragType.value = hoveredCircle
      
      // Start new drag on the other circle (don't call endDrag - we're still dragging!)
      currentDrag.value?.startDrag()
      
      // Note: We DON'T switch color modes here anymore!
      // The mode switch will happen when the drag ENDS (in handleCircleMouseUp)
      // This prevents database writes during drag that cause jank
    }
    
    // For now, only handle hue circle pip dragging in global move
    // CCT slider is handled separately
    if (activeDragType.value === 'hue') {
      if (!hueContainer) return
      
      const rect = hueContainer.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const radius = rect.width / 2
      
      const dx = event.clientX - centerX
      const dy = event.clientY - centerY
      
      const coord = {
        x: dx / radius,
        y: dy / radius
      }
      
      currentDrag.value.updatePosition(coord)
    } else if (activeDragType.value === 'white') {
      // Handle CCT slider pip dragging
      const cctContainer = cctSliderContainerRef.value?.containerRef
      if (!cctContainer) return
      
      const rect = cctContainer.getBoundingClientRect()
      
      // Calculate x position as 0-1 ratio (clamped)
      const x = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
      
      // Convert x position to normalized coord for the drag system (-1 to 1 range)
      const coord = { x: x * 2 - 1, y: 0 }
      
      currentDrag.value.updatePosition(coord)
    }
  }
  
  // Handle rectangle selection
  if (rectangleSelection.isSelecting.value && containerRef.value) {
    // Convert viewport coordinates to container-relative coordinates
    const containerRect = containerRef.value.getBoundingClientRect()
    const x = event.clientX - containerRect.left
    const y = event.clientY - containerRect.top
    
    rectangleSelection.updateSelection(x, y)
    
    // Select pips within rectangle (only for hue circle for now)
    const allLights = [...lightsInHueCircle.value]
    
    for (const item of allLights) {
      if (!hueContainer) continue
      
      const rect = hueContainer.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const radius = rect.width / 2
      
      // Convert pip screen position to container-relative coordinates
      const pipScreenX = centerX + item.position.x * radius
      const pipScreenY = centerY + item.position.y * radius
      const pipX = pipScreenX - containerRect.left
      const pipY = pipScreenY - containerRect.top
      
      if (rectangleSelection.isPointInSelection(pipX, pipY)) {
        if (!selection.isSelected(item.light)) {
          selection.selectItem(item.light)
        }
      }
    }
  }
}

const handleCircleMouseUp = async () => {
  // Handle gradient stop drag end
  if (isDraggingGradientStop.value) {
    isDraggingGradientStop.value = false
    draggingGradientLight.value = null
    draggingGradientStopId.value = null
    activeDragType.value = null
    return
  }
  
  if (isDraggingPip.value) {
    // Store which circle we ended in before clearing state
    const finalCircleType = activeDragType.value
    
    // End the drag and flush position updates
    await currentDrag.value?.endDrag()
    isDraggingPip.value = false
    
    // Now that drag is complete, apply any pending mode switch
    // This ensures the database gets the correct final color mode
    if (finalCircleType === 'white' && selection.selectedItems.value.some(item => 
      colorMode.isInColorMode(item)
    )) {
      logger.info(`[Canvas] Applying WHITE mode switch after drag complete`)
      moveSelectedToWhite()
    } else if (finalCircleType === 'hue' && selection.selectedItems.value.some(item =>
      !colorMode.isInColorMode(item)
    )) {
      logger.info(`[Canvas] Applying COLOR mode switch after drag complete`)
      moveSelectedToColor()
    }
  }
  
  if (rectangleSelection.isSelecting.value) {
    rectangleSelection.endSelection()
  }
  
  activeDragType.value = null
}

/**
 * CCT Slider mouse handlers
 */
const handleSliderMouseDown = (event: MouseEvent, sliderType: 'white') => {
  // Start rectangle selection if clicking on empty area
  const target = event.target as HTMLElement
  const isSlider = target.classList.contains('slider-container') || target.parentElement?.classList.contains('slider-container')
  
  if (isSlider && containerRef.value) {
    const rect = containerRef.value.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    
    rectangleSelection.startSelection(x, y)
    
    if (!event.shiftKey) {
      selection.clearSelection()
    }
  }
}

const handleCardClick = (item: Light) => {
  selection.toggleItem(item)
}


// Lifecycle hooks
onMounted(async () => {
  await initialize()
  await hueBridgesStore.fetchBridges()
})

watch(currentSceneId, async (newId) => {
  if (newId) {
    await refreshData()
  }
  selection.clearSelection()
})
</script>

<style scoped>
.art-deco-connector {
  z-index: 5;
  opacity: 0.9;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  transition: opacity 300ms ease, filter 300ms ease;
}

:global(.dark) .art-deco-connector,
:global(.midnight) .art-deco-connector {
  opacity: 0.95;
  filter: drop-shadow(0 0 4px rgba(212, 175, 55, 0.5));
}

:global(.midnight) .art-deco-connector {
  filter: drop-shadow(0 0 4px rgba(90, 126, 184, 0.45));
}

.art-deco-connector:hover {
  opacity: 1;
}

:global(.dark) .art-deco-connector:hover {
  filter: drop-shadow(0 0 6px rgba(212, 175, 55, 0.7));
}

:global(.midnight) .art-deco-connector:hover {
  filter: drop-shadow(0 0 6px rgba(90, 126, 184, 0.6));
}

.compact-light-card {
  background: hsl(var(--card));
}

.compact-light-card:hover {
  background: hsl(var(--card) / 0.8);
  transform: translateY(-1px);
}

/* Custom slider styling */
.slider {
  background: hsl(var(--muted));
  outline: none;
  cursor: pointer;
}

.slider::-webkit-slider-thumb {
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: hsl(var(--primary));
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: hsl(var(--primary));
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
</style>
